import React, { useEffect, useRef, useState } from 'react';
import axiosClient from "../axios-client.js";
import _ from 'lodash';
import moment from 'moment';
import { Link } from 'react-router-dom';

const getCoachImg = (image) => {
  if (!image) return null;
  try {
    return require(`../images/coaches/${image}`);
  } catch {
    return null;
  }
};

const getInitials = (name) => {
  if (!name) return null;
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const CoachAvatar = ({ image, name }) => {
  const src = getCoachImg(image);
  if (src) {
    return <div className='cal-coach-cont' style={{ backgroundImage: `url(${src})` }}></div>
    // return <img className="cal-coach-avatar" src={src} alt={name} />;
  }
  const initials = getInitials(name);
  return (
    <div
      className="cal-coach-avatar cal-coach-avatar--fallback"
      style={{ background: initials ? 'var(--light-blue)' : 'var(--surface-raised)' }}
    >
      {initials ?? ''}
    </div>
  );
};

const ReservarClases = () => {
  const [eventClasses, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRide, setShowRide]     = useState(true);
  const [showTrain, setShowTrain]   = useState(true);
  const [showYoga, setShowYoga]     = useState(true);
  const [showPilates, setShowPilates] = useState(true);
  const [showBarre, setShowBarre]     = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoaches, setSelectedCoaches] = useState(new Set()); // empty = todos
  const [showCoachDrop, setShowCoachDrop] = useState(false);
  const coachDropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (coachDropRef.current && !coachDropRef.current.contains(e.target)) {
        setShowCoachDrop(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isRide    = (t) => t === 'Ride' || t === 'Indoor Cycling';
  const isTrain   = (t) => t === 'Train';
  const isYoga    = (t) => t === 'Yoga';
  const isPilates = (t) => t === 'Pilates Mat';
  const isBarre   = (t) => t === 'Barre';

  useEffect(() => {
    getClasses();
    axiosClient.get('/coaches-active')
      .then(({ data }) => setCoaches(data.data ?? data))
      .catch(() => {});
  }, []);

  const getClasses = () => {
    setLoading(true);
    axiosClient.get('/classes/calendar/week')
      .then(({ data }) => {
        setLoading(false);
        const grouped = _.groupBy(data, 'class_date');
        const now = moment();
        const arr = _.map(grouped, (classes, date) => ({
          date,
          classes: classes
            .map((c) => ({
              ...c,
              isAvailable:
                moment(date).isAfter(now, 'day') ||
                (moment(date).isSame(now, 'day') &&
                 moment(now).isBefore(moment(c.class_time, 'HH:mm'))),
            }))
            .sort((a, b) => a.class_time.localeCompare(b.class_time)),
        })).sort((a, b) => a.date.localeCompare(b.date));
        setClasses(arr);
        // Default: today if present, else first day that hasn't passed, else first day
        const todayStr = now.format('YYYY-MM-DD');
        const todayDay   = arr.find((d) => d.date === todayStr);
        const futureDay  = arr.find((d) => d.date >= todayStr);
        setSelectedDate((todayDay ?? futureDay ?? arr[0])?.date ?? null);
      })
      .catch(() => setLoading(false));
  };

  const today = moment().format('YYYY-MM-DD');
  const selectedDay = eventClasses.find((d) => d.date === selectedDate);
  const visibleClasses = (selectedDay?.classes ?? []).filter((c) => {
    const typeOk = isRide(c.type_class)    ? showRide
                 : isTrain(c.type_class)   ? showTrain
                 : isYoga(c.type_class)    ? showYoga
                 : isPilates(c.type_class) ? showPilates
                 : isBarre(c.type_class)   ? showBarre
                 : true;
    const coachOk = selectedCoaches.size === 0 || selectedCoaches.has(c.coach_id);
    return typeOk && coachOk;
  });

  const toggleCoach = (id) => {
    setSelectedCoaches((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const typeColor = (type) =>
    isRide(type)    ? 'var(--light-blue)'
    : isTrain(type)   ? 'var(--secondary-color)'
    : isYoga(type)    ? '#7c5cbf'
    : isPilates(type) ? '#2baa72'
    : isBarre(type)   ? '#d4527a'
    : '#888';

  return (
    <div className="cal-page">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="cal-header">
        <div className="cal-filters">
          <button
            className={`cal-pill ${showRide ? 'active' : ''}`}
            onClick={() => setShowRide((v) => !v)}
          >
            Ride
          </button>
          <button
            className={`cal-pill ${showTrain ? 'active' : ''}`}
            onClick={() => setShowTrain((v) => !v)}
          >
            Train
          </button>
          <button
            className={`cal-pill ${showYoga ? 'active' : ''}`}
            onClick={() => setShowYoga((v) => !v)}
          >
            Yoga
          </button>
          <button
            className={`cal-pill ${showPilates ? 'active' : ''}`}
            onClick={() => setShowPilates((v) => !v)}
          >
            Pilates Mat
          </button>
          <button
            className={`cal-pill ${showBarre ? 'active' : ''}`}
            onClick={() => setShowBarre((v) => !v)}
          >
            Barre
          </button>

          {(!showRide || !showTrain || !showYoga || !showPilates || !showBarre || selectedCoaches.size > 0) && (
            <button
              className="cal-clear-filters"
              onClick={() => {
                setShowRide(true);
                setShowTrain(true);
                setShowYoga(true);
                setShowPilates(true);
                setShowBarre(true);
                setSelectedCoaches(new Set());
              }}
            >
              ✕ Limpiar filtros
            </button>
          )}

          {coaches.length > 0 && (
            <>
              <span className="cal-filter-divider" />
              <div className="cal-coach-drop-wrap" ref={coachDropRef}>
                <button
                  className={`cal-pill ${selectedCoaches.size > 0 ? 'active' : ''}`}
                  onClick={() => setShowCoachDrop((v) => !v)}
                >
                  Coaches {selectedCoaches.size > 0 && `(${selectedCoaches.size})`}
                  <span style={{ marginLeft: 4, fontSize: 10 }}>{showCoachDrop ? '▲' : '▼'}</span>
                </button>
                {showCoachDrop && (
                  <div className="cal-coach-dropdown">
                    {coaches.map((coach) => (
                      <label key={coach.id} className="cal-coach-option">
                        <input
                          type="checkbox"
                          checked={selectedCoaches.has(coach.id)}
                          onChange={() => toggleCoach(coach.id)}
                        />
                        {coach.name}
                      </label>
                    ))}
                    {selectedCoaches.size > 0 && (
                      <button
                        className="cal-coach-clear"
                        onClick={() => setSelectedCoaches(new Set())}
                      >
                        Limpiar filtro
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Day selector ─────────────────────────────────────── */}
      <div className="cal-day-strip">
        <div className="cal-day-list">
          {eventClasses.map((d) => {
            const m   = moment(d.date);
            const isToday = d.date === today;
            const isActive = d.date === selectedDate;
            return (
              <button
                key={d.date}
                className={`cal-day-btn ${isActive ? 'active' : ''}`}
                onClick={() => setSelectedDate(d.date)}
              >
                {isToday && <span className="cal-today-label">Hoy</span>}
                <span className="cal-day-name">{m.format('ddd').toUpperCase()}</span>
                <span className="cal-day-num">{m.format('D MMM')}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Classes list ─────────────────────────────────────── */}
      <div className="cal-body">
        {loading ? (
          <div className="loading-container center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Cargando…</span>
            </div>
          </div>
        ) : !selectedDay ? (
          <p className="text-center" style={{ color: 'var(--text-muted)', paddingTop: 40 }}>
            Aún estamos creando el nuevo calendario, favor de intentarlo en otro momento.
          </p>
        ) : (
          <>
            {/* Selected date label */}
            <div className="cal-date-label">
              <span>{moment(selectedDate).format('dddd, D [de] MMMM [de] YYYY')}</span>
              {selectedDate === today && (
                <span className="cal-today-badge">Hoy</span>
              )}
            </div>

            {visibleClasses.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 14, paddingTop: 16 }}>
                No hay clases para este día con los filtros seleccionados.
              </p>
            ) : (
              visibleClasses.map((c, i) => (
                <Link
                  key={i}
                  to={`/reservar-clase/${c.id}`}
                  className={`cal-class-row ${!c.isAvailable || c.status === 'Cancelada' ? 'cal-class-row--unavailable' : ''}`}
                >
                  {/* Left color bar */}
                  <div className="cal-color-bar" style={{ background: typeColor(c.type_class) }} />

                  {/* Time */}
                  <div className="cal-class-time">
                    <span className="cal-time-main">{moment(c.class_time, 'HH:mm:ss').format('h:mm')}</span>
                    <span className="cal-time-ampm">{moment(c.class_time, 'HH:mm:ss').format('a')}</span>
                    <span className="cal-time-dur">{c.duration ?? 50} min.</span>
                  </div>

                  {/* Coach avatar */}
                  <CoachAvatar image={c.coach_image} name={c.coach_name} />

                  {/* Info */}
                  <div className="cal-class-info">
                    <span className="cal-class-type">
                      {isRide(c.type_class) ? 'Ride' : isTrain(c.type_class) ? 'Train' : c.type_class}
                    </span>
                    <span className="cal-coach-name">{c.coach_name}</span>
                    {c.special_class && c.status !== 'Cancelada' && (
                      <span className="cal-special">"{c.special_class}"</span>
                    )}
                    {c.status === 'Cancelada' && (
                      <span className="cal-cancelled">Clase cancelada</span>
                    )}
                  </div>

                  {/* Reserve button */}
                  <div className="cal-class-action">
                    {c.status === 'Cancelada' ? (
                      <span className="cal-cancelled-badge">Cancelada</span>
                    ) : !c.isAvailable ? (
                      <span className="cal-unavailable-badge">No disponible</span>
                    ) : (
                      <span className="cal-reserve-btn">Reservar</span>
                    )}
                  </div>
                </Link>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReservarClases;